/** Styles for the Library screen and its components. */
import { StyleSheet } from 'react-native';

import { reviewDesk, spacing } from '@/design-system';
import { COLORS } from './libraryFormat';

// ─────────────────────────────────────────────────────────────────────────────
// Styles - Clean and simple
// ─────────────────────────────────────────────────────────────────────────────

export const styles = StyleSheet.create({
  // Loading overlay (shown while a transcript loads)
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingCard: {
    backgroundColor: COLORS.surface,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderRadius: 16,
    alignItems: 'center',
    gap: spacing.md,
  },
  loadingOverlayText: {
    color: COLORS.text.primary,
    fontSize: 14,
    fontWeight: '500',
  },
});

export const webStyles = StyleSheet.create({
  topIconButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    marginBottom: 26,
  },
  pageHeaderCompact: {
    flexDirection: 'column',
    gap: 14,
  },
  pageTitle: {
    color: reviewDesk.ink,
    fontSize: 34,
    fontWeight: '800',
    letterSpacing: 0,
  },
  pageSubtitle: {
    color: reviewDesk.inkSoft,
    fontSize: 16,
    marginTop: 8,
    lineHeight: 23,
  },
  primaryButton: {
    minHeight: 48,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    boxShadow: '0 10px 22px rgba(30, 64, 175, 0.18)',
  } as any,
  primaryButtonWide: {
    minHeight: 48,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    width: '100%',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  uploadPanel: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    borderRadius: 12,
    padding: 26,
    marginBottom: 16,
  },
  uploadPanelHighlighted: {
    borderWidth: 2,
    borderColor: reviewDesk.accent,
    backgroundColor: reviewDesk.accentWash,
  },
  uploadPrompt: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
    marginTop: 10,
  },
  uploadCopy: {
    alignItems: 'center',
    marginBottom: 22,
  },
  uploadIconLarge: {
    width: 66,
    height: 66,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  uploadTitle: {
    color: reviewDesk.ink,
    fontSize: 26,
    fontWeight: '800',
  },
  uploadText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 21,
    maxWidth: 620,
  },
  uploadActions: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  uploadActionsPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  uploadChoice: {
    minWidth: 0,
    flexBasis: 300,
    flexShrink: 1,
    flex: 1,
    maxWidth: 380,
    minHeight: 94,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    borderRadius: 10,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  uploadChoicePhone: {
    width: '100%',
    maxWidth: '100%',
    flexBasis: 'auto',
  },
  choiceIcon: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  choiceText: {
    flex: 1,
  },
  choiceTitle: {
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
  },
  choiceMeta: {
    color: reviewDesk.muted,
    fontSize: 13,
    marginTop: 5,
  },
  flowStrip: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    borderRadius: 12,
    padding: 18,
    marginBottom: 16,
    flexDirection: 'row',
    gap: 12,
  },
  flowStripCompact: {
    flexDirection: 'column',
  },
  flowStep: {
    flex: 1,
    minWidth: 0,
    flexBasis: 220,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  flowStepCompact: {
    flexBasis: 'auto',
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumberText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
  },
  stepIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: reviewDesk.accentWash,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepText: {
    flex: 1,
  },
  stepTitle: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  stepCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
  },
  shareCenter: {
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: '#FFFDF9',
    borderRadius: 12,
    padding: 18,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 18,
  },
  shareCenterCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  shareCenterIntro: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  shareCenterIcon: {
    width: 58,
    height: 58,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareCenterCopy: {
    flex: 1,
    minWidth: 0,
  },
  shareCenterTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
  },
  shareCenterText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
    marginTop: 4,
    maxWidth: 680,
  },
  shareCenterMeta: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
    marginTop: 8,
  },
  shareMetricRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  shareMetric: {
    minWidth: 0,
    flexBasis: 118,
    flexGrow: 1,
    flexShrink: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 12,
  },
  shareMetricValue: {
    color: reviewDesk.ink,
    fontSize: 24,
    fontWeight: '800',
  },
  shareMetricLabel: {
    color: reviewDesk.inkSoft,
    fontSize: 12,
    fontWeight: '700',
    marginTop: 2,
  },
  shareCenterActions: {
    width: '100%',
    maxWidth: 196,
    flexShrink: 1,
    gap: 8,
  },
  shareCenterPrimary: {
    minHeight: 40,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  shareCenterPrimaryText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  shareCenterSecondary: {
    minHeight: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  shareCenterSecondaryText: {
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '800',
  },
  shareCenterEmpty: {
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '700',
  },
  contentGrid: {
    flexDirection: 'row',
    gap: 18,
    alignItems: 'stretch',
  },
  contentGridCompact: {
    flexDirection: 'column',
  },
  sessionsPanel: {
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 16,
  },
  sidePanel: {
    width: '100%',
    maxWidth: 284,
    flexBasis: 284,
    flexShrink: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 22,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  sidePanelCompact: {
    width: '100%',
    maxWidth: '100%',
    flexBasis: 'auto',
  },
  listToolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 16,
  },
  listToolbarCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  searchBox: {
    flex: 1,
    minHeight: 46,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
  },
  searchInput: {
    flex: 1,
    outlineStyle: 'none',
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
    color: reviewDesk.ink,
  } as any,
  webFilters: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  webFiltersCompact: {
    justifyContent: 'flex-start',
  },
  webFilterChip: {
    minHeight: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  webFilterChipActive: {
    backgroundColor: reviewDesk.accent,
    borderColor: reviewDesk.accent,
  },
  webFilterText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    fontWeight: '700',
  },
  webFilterTextActive: {
    color: '#FFFFFF',
  },
  webFilterCount: {
    color: reviewDesk.muted,
    fontSize: 12,
    fontWeight: '800',
  },
  webList: {
    gap: 12,
  },
  loadMoreButton: {
    alignSelf: 'center',
    minHeight: 42,
    marginTop: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadMoreText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  sessionRow: {
    minHeight: 82,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    overflow: 'hidden',
  },
  sessionRowTiny: {
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  sessionRowPhone: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sessionRowFailed: {
    backgroundColor: '#FFFDFD',
    borderColor: 'rgba(239, 68, 68, 0.24)',
  },
  fileIcon: {
    width: 48,
    height: 48,
    borderRadius: 10,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sessionInfo: {
    flex: 1,
    minWidth: 0,
    flexBasis: 220,
    flexShrink: 1,
  },
  sessionInfoPhone: {
    width: '100%',
    flexBasis: 'auto',
  },
  sessionTitle: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
  },
  sessionMeta: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    marginTop: 5,
    flexWrap: 'wrap',
  },
  statusPill: {
    minHeight: 30,
    borderRadius: 7,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  statusPillTiny: {
    marginLeft: 61,
  },
  statusPillPhone: {
    alignSelf: 'flex-start',
    marginLeft: 0,
  },
  statusPill_success: {
    backgroundColor: reviewDesk.successSoft,
  },
  statusPill_warning: {
    backgroundColor: reviewDesk.amberSoft,
  },
  statusPill_error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
  },
  statusPill_neutral: {
    backgroundColor: reviewDesk.cardMuted,
  },
  statusText: {
    fontSize: 13,
    fontWeight: '700',
  },
  statusText_success: {
    color: reviewDesk.success,
  },
  statusText_warning: {
    color: reviewDesk.amber,
  },
  statusText_error: {
    color: COLORS.error,
  },
  statusText_neutral: {
    color: reviewDesk.inkSoft,
  },
  iconButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    flexShrink: 1,
    justifyContent: 'flex-end',
  },
  shareActionsTiny: {
    flex: 1,
    minWidth: '100%',
    marginLeft: 61,
  },
  shareActionsPhone: {
    width: '100%',
    minWidth: 0,
    marginLeft: 0,
    justifyContent: 'flex-start',
  },
  shareActionButton: {
    minHeight: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.accentWash,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  manageActionButton: {
    minHeight: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  shareActionText: {
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '800',
  },
  manageActionText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    fontWeight: '800',
  },
  openButton: {
    minHeight: 38,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 12,
    flexShrink: 0,
  },
  openButtonTiny: {
    flex: 1,
    minWidth: 0,
    flexBasis: 140,
  },
  openButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  deleteActionButton: {
    minHeight: 38,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.24)',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexShrink: 0,
  },
  deleteActionButtonTiny: {
    flex: 1,
    minWidth: 0,
    flexBasis: 180,
    marginLeft: 61,
  },
  rowActionPhone: {
    width: '100%',
    maxWidth: '100%',
    flexBasis: 'auto',
    marginLeft: 0,
  },
  deleteActionText: {
    color: COLORS.error,
    fontSize: 14,
    fontWeight: '800',
  },
  deleteOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.34)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 1000,
  } as any,
  deleteDialog: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: '#FFFFFF',
    padding: 22,
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
  } as any,
  deleteDialogIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  deleteDialogTitle: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
  },
  deleteDialogFilename: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    fontWeight: '800',
    marginTop: 10,
  },
  deleteDialogBody: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    marginTop: 8,
  },
  deleteErrorBox: {
    marginTop: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.22)',
    backgroundColor: 'rgba(239, 68, 68, 0.07)',
    padding: 12,
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  deleteErrorText: {
    color: COLORS.error,
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '700',
  },
  deleteDialogActions: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
  },
  cancelDeleteButton: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelDeleteText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    fontWeight: '800',
  },
  confirmDeleteButton: {
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: COLORS.error,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
  confirmDeleteText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  webLoading: {
    minHeight: 260,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  webLoadingText: {
    color: reviewDesk.muted,
    fontSize: 14,
  },
  webEmpty: {
    minHeight: 320,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  webEmptyIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  webEmptyTitle: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
  },
  webEmptyText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    maxWidth: 380,
    marginTop: 8,
    marginBottom: 18,
  },
  secondaryButton: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  secondaryButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  sideIllustration: {
    width: '100%',
    maxWidth: 118,
    height: 118,
    borderRadius: 24,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: reviewDesk.lineStrong,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  sideArrow: {
    position: 'absolute',
    right: -12,
    bottom: 18,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideTitle: {
    color: reviewDesk.ink,
    fontSize: 21,
    fontWeight: '800',
    textAlign: 'center',
  },
  sideText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
  },
  linkButton: {
    minHeight: 38,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  linkButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  privacyBanner: {
    marginTop: 16,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.accentWash,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  privacyBannerPhone: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  privacyTitle: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '800',
  },
  privacyText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
    marginTop: 2,
  },
});
